import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ShieldCheck, Shield, Copy, KeyRound, AlertTriangle } from "lucide-react";

/** Two-factor (TOTP) settings block used inside Profile page. */
export default function TwoFactorPanel() {
    const { user, updateUser } = useAuth();
    const enabled = !!user?.totp_enabled;
    const [setup, setSetup] = React.useState(null); // {secret, otpauth_url, qr_data_url}
    const [code, setCode] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const [showDisable, setShowDisable] = React.useState(false);
    const [recoveryCodes, setRecoveryCodes] = React.useState(null);

    async function beginSetup() {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/totp/setup");
            setSetup(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start 2FA setup");
        } finally { setBusy(false); }
    }

    async function confirmEnable() {
        if (!code.trim()) return toast.error("Enter the 6-digit code");
        setBusy(true);
        try {
            const { data } = await api.post("/auth/totp/enable", { code });
            updateUser({ totp_enabled: true });
            setSetup(null);
            setCode("");
            setRecoveryCodes(data.recovery_codes);
            toast.success("Two-factor authentication enabled");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
        } finally { setBusy(false); }
    }

    if (recoveryCodes) {
        return <RecoveryCodesDialog codes={recoveryCodes} onClose={() => setRecoveryCodes(null)} />;
    }

    return (
        <Card>
            <CardContent className="p-6" data-testid="two-factor-panel">
                <div className="flex items-center gap-2 mb-4">
                    {enabled ? <ShieldCheck className="w-4 h-4 text-emerald-600" /> : <Shield className="w-4 h-4 text-primary" />}
                    <h2 className="font-dash text-lg font-semibold">Two-factor authentication</h2>
                    {enabled && <Badge className="bg-emerald-100 text-emerald-800 border-0">Enabled</Badge>}
                </div>

                {!enabled && !setup && (
                    <div className="space-y-3">
                        <p className="text-sm text-brand-stone">
                            Protect your account with a 6-digit code from an authenticator app
                            (Google Authenticator, Authy, 1Password, etc.). Even if someone steals
                            your password, they won&apos;t be able to sign in without your phone.
                        </p>
                        <Button onClick={beginSetup} disabled={busy} data-testid="enable-2fa-btn" className="bg-primary hover:bg-primary/90 gap-2">
                            <ShieldCheck className="w-4 h-4" /> {busy ? "Preparing…" : "Enable 2FA"}
                        </Button>
                    </div>
                )}

                {!enabled && setup && (
                    <div className="space-y-4">
                        <ol className="text-sm text-brand-stone space-y-2 list-decimal list-inside">
                            <li>Open your authenticator app.</li>
                            <li>Scan this QR code (or paste the setup key manually).</li>
                            <li>Enter the 6-digit code the app shows.</li>
                        </ol>
                        <div className="flex flex-col sm:flex-row gap-4 items-start">
                            <img src={setup.qr_data_url} alt="Scan to enable 2FA" className="w-44 h-44 border border-border rounded-lg" data-testid="totp-qr" />
                            <div className="flex-1 space-y-3">
                                <div>
                                    <Label className="text-xs">Setup key (if you can&apos;t scan)</Label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <code className="text-xs bg-secondary/60 px-2 py-1.5 rounded border border-border break-all flex-1" data-testid="totp-secret">{setup.secret}</code>
                                        <button
                                            onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Copied"); }}
                                            className="text-brand-stone hover:text-primary"
                                            aria-label="Copy setup key"
                                        >
                                            <Copy className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <Label>6-digit code from your app</Label>
                                    <Input
                                        value={code}
                                        onChange={e => setCode(e.target.value)}
                                        placeholder="123 456"
                                        data-testid="totp-verify-input"
                                        className="mt-1.5 tracking-widest text-center text-lg"
                                        maxLength={7}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Button onClick={confirmEnable} disabled={busy} data-testid="confirm-enable-2fa-btn" className="bg-primary hover:bg-primary/90 gap-2">
                                        <ShieldCheck className="w-4 h-4" /> {busy ? "Verifying…" : "Turn on 2FA"}
                                    </Button>
                                    <Button variant="outline" onClick={() => setSetup(null)} disabled={busy}>Cancel</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {enabled && (
                    <div className="space-y-3">
                        <p className="text-sm text-brand-stone">
                            Two-factor is protecting your account. Keep your recovery codes somewhere safe.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outline" onClick={() => setShowDisable(true)} data-testid="disable-2fa-btn" className="gap-2 text-rose-700 border-rose-200 hover:bg-rose-50">
                                <Shield className="w-4 h-4" /> Turn off 2FA
                            </Button>
                            <RegenerateCodesButton onDone={setRecoveryCodes} />
                        </div>
                        <DisableDialog open={showDisable} onOpenChange={setShowDisable} onDone={() => { updateUser({ totp_enabled: false }); setShowDisable(false); }} />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function RegenerateCodesButton({ onDone }) {
    const [open, setOpen] = React.useState(false);
    const [password, setPassword] = React.useState("");
    const [code, setCode] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/totp/recovery-codes", { password, code });
            onDone(data.recovery_codes);
            setOpen(false); setPassword(""); setCode("");
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    }
    return (
        <>
            <Button variant="outline" onClick={() => setOpen(true)} data-testid="regen-codes-btn" className="gap-2">
                <KeyRound className="w-4 h-4" /> New recovery codes
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Generate new recovery codes</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <p className="text-sm text-brand-stone">
                            Old recovery codes will stop working immediately.
                        </p>
                        <div>
                            <Label>Password</Label>
                            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="regen-password-input" className="mt-1.5" />
                        </div>
                        <div>
                            <Label>2FA code (from your app or a current recovery code)</Label>
                            <Input value={code} onChange={e => setCode(e.target.value)} required data-testid="regen-code-input" className="mt-1.5" />
                        </div>
                        <Button type="submit" disabled={busy} data-testid="regen-submit-btn" className="w-full bg-primary hover:bg-primary/90">
                            {busy ? "Working…" : "Generate new codes"}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}

function DisableDialog({ open, onOpenChange, onDone }) {
    const [password, setPassword] = React.useState("");
    const [code, setCode] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/totp/disable", { password, code });
            toast.success("Two-factor turned off");
            onDone();
        } catch (err) { toast.error(err.response?.data?.detail || "Could not disable"); }
        finally { setBusy(false); }
    }
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-serif text-2xl">Turn off two-factor?</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="flex items-start gap-2 text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>Your account will rely on the password alone. You can turn 2FA back on any time.</div>
                    </div>
                    <div>
                        <Label>Password</Label>
                        <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="disable-password-input" className="mt-1.5" />
                    </div>
                    <div>
                        <Label>2FA code (from your app or a recovery code)</Label>
                        <Input value={code} onChange={e => setCode(e.target.value)} required data-testid="disable-code-input" className="mt-1.5" />
                    </div>
                    <Button type="submit" disabled={busy} data-testid="confirm-disable-2fa-btn" className="w-full bg-rose-600 hover:bg-rose-700">
                        {busy ? "Working…" : "Turn off 2FA"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function RecoveryCodesDialog({ codes, onClose }) {
    function copyAll() {
        navigator.clipboard.writeText(codes.join("\n"));
        toast.success("All 8 codes copied");
    }
    return (
        <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
            <DialogContent data-testid="recovery-codes-dialog">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Save your recovery codes</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="flex items-start gap-2 text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <div><b>These codes will not be shown again.</b> Store them somewhere only you can access — a password manager works great. Each code is single-use.</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-secondary/40 border border-border rounded-lg p-4">
                        {codes.map(c => (<div key={c} data-testid={`code-${c}`}>{c}</div>))}
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={copyAll} variant="outline" data-testid="copy-codes-btn" className="gap-2">
                            <Copy className="w-4 h-4" /> Copy all
                        </Button>
                        <Button onClick={onClose} data-testid="close-codes-btn" className="bg-primary hover:bg-primary/90">Done</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
